import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  KiteIcon,
  KiteToast,
  KiteTag,
  KiteTabs,
  KiteTab,
  KiteLink,
} from '@kite/react-kite';

import { useQuantum } from 'hooks';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { useAppStore } from 'store/AppStore';
import { getDeviceIcon, getLobIcon } from 'utils/deviceIcons';
import PreviewPDF from 'pages/PreviewPDF/PreviewPDF';
import Changelog from 'pages/Changelog/Changelog';

import './ReviewPDF.scss';

const ReviewPDF = () => {
  const { trackPageView } = useQuantum();
  const navigate = useNavigate();
  const orderType = useAppStore((state) => state.orderType);

  // Build pickedPlatform string for the PDF payload.
  //
  // Special configs resolved without a device join (e.g. the SONU / ONU fiber
  // keys such as W-SONU-RES) come back with every device field null, so the
  // device concatenation yields an empty string and PreviewPDF would skip the
  // generatePdf call. For those the backend supplies a resolved `pickedPlatform`
  // derived from the configured equipment; prefer it whenever the device fields
  // produce nothing.
  const pickedPlatform = useMemo(() => {
    if (!orderType) return '';
    const parts: string[] = [];
    if (orderType.modemType) parts.push(orderType.modemType);
    if (orderType.wifiType) parts.push(orderType.wifiType);
    if (orderType.hasXumo && orderType.xumoVariant) parts.push(orderType.xumoVariant);
    if (orderType.hasBbs) parts.push('BBS');
    if (parts.length > 0) return parts.join('; ');

    const fromBackend = orderType.pickedPlatform?.trim();
    if (fromBackend) return fromBackend;

    return orderType.configKey || '';
  }, [orderType]);

  // Device tags for the header
  const deviceTags = useMemo(() => {
    if (!orderType) return [];
    const tags: string[] = [];
    if (orderType.modemType) tags.push(orderType.modemType);
    if (orderType.wifiType) tags.push(orderType.wifiType);
    if (orderType.hasXumo && orderType.xumoVariant) tags.push(orderType.xumoVariant);
    if (orderType.hasBbs) tags.push('BBS');
    return tags;
  }, [orderType]);

  const orderTypeName = orderType?.lookupServices || orderType?.orderType || '';

  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [toastText, setToastText] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Build the PDF generation payload
  const pdfPayload = useMemo(() => {
    if (!orderType) return undefined;

    return {
      pickedPlatform,
      orderType: orderType.orderType || '',
      customerSegment: orderType.lob || '',
      servicesRepresented: orderType.servicesRepresented || '',
      internetOrder: orderType.hasInternet ? 'Y' : 'N',
      wifiOrder: orderType.hasWifi ? 'Y' : 'N',
      voiceOrder: orderType.hasVoice ? 'Y' : 'N',
      videoOrder: orderType.hasXumo ? 'Y' : 'N',
    };
  }, [orderType, pickedPlatform]);

  useEffect(() => {
    trackPageView('ReviewPDF');
  }, [trackPageView]);

  const handleTabChange = (value: number) => {
    setCurrentTab(value);
  };

  const handleToastError = (error: string) => {
    setToastText(error);
  };

  return (
    <main className="review-pdf" data-testid="review-pdf-main">
      <KiteLink
        href="/"
        className="back-to-dashboard-link"
        onClick={(e) => {
          e.preventDefault();
          navigate('/');
        }}
      >
        <KiteIcon icon="ki-chevron-left" />
        Back to Dashboard
      </KiteLink>

      <div className="collateral-info-section">
        <h1 className="collateral-title">{orderTypeName || '-'}</h1>

        <div className="collateral-tags-and-tabs-row">
          <div className="collateral-tags">
            {deviceTags.map((device, idx) => (
              <KiteTag
                key={`${device}-${idx}`}
                variant="outline"
                icon={getDeviceIcon(device) || 'ki-info-circle'}
              >
                {device}
              </KiteTag>
            ))}
            {orderType?.lineOfBusiness && (
              <KiteTag
                variant="outline"
                icon={getLobIcon(orderType.lineOfBusiness)}
              >
                {orderType.lineOfBusiness}
              </KiteTag>
            )}
          </div>

          <KiteTabs
            selectedIndex={currentTab}
            focusChange={handleTabChange}
            className="review-tabs"
          >
            <KiteTab label="Preview" />
            <KiteTab label="Changelog" />
          </KiteTabs>
        </div>
      </div>

      <div className="review-content-wrapper">
        {currentTab === 0 && (
          <PreviewPDF
            pdfPayload={pdfPayload}
            pickedPlatform={pickedPlatform}
            orderType={orderTypeName}
            collateralId={orderType?.collateralCode}
            currentVersion={orderType?.collateralVersion}
            status={orderType?.active ? 'In Production' : 'Pilot'}
            onError={handleToastError}
          />
        )}

        {currentTab === 1 && (
          <Changelog collateralId={orderType?.collateralCode} />
        )}

        {/* {currentTab === 2 && (
          <div className="analytics-placeholder">
            <p>Analytics for this collateral will be displayed here.</p>
          </div>
        )} */}
      </div>

      <ConfirmationDialog
        isOpen={isApproveDialogOpen}
        onConfirm={() => setIsApproveDialogOpen(false)}
        onCancel={() => setIsApproveDialogOpen(false)}
        message="Are you sure you want to approve this document without Review?"
      />

      <KiteToast
        visible={!!toastText}
        duration="long"
        topOffset={65}
        icon="ki-caution-circle-f"
        position="top"
        alignment="center"
        onExpire={() => setToastText(null)}
      >
        {toastText || ''}
      </KiteToast>
      <Outlet />
    </main>
  );
};

export default ReviewPDF;
